import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PLATFORMS, POST_MIN_LEAD_MS, TIKTOK_BRANDED_CONTENT_URL, TIKTOK_MUSIC_USAGE_URL, TIKTOK_PRIVACY_WORDS, TIKTOK_TEXT,
  X_LINK_NOTE, YOUTUBE_PRIVACY, YOUTUBE_PRIVACY_WORDS, YOUTUBE_TEXT, hasLink,
  mediaFileUrl, refusedBecause, tiktokConsent, tiktokLabel, tiktokRefusal, youtubeRefusal,
  type ChannelView, type ProjectView, type ShotView, type TikTokChoices, type TikTokCreator, type TikTokPrivacy,
  type YouTubeChoices, type YouTubePrivacy,
} from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { useWorkspace } from "../ui/workspace.js";

/**
 * Putting a finished post on a channel ([25](../../../../docs/25-channels-and-publishing.md),
 * [ADR 0038](../../../../docs/adr/0038-a-channel-is-a-connection-a-post-is-a-row-and-the-worker-is-the-only-thing-that-publishes.md)).
 *
 * Publishing always opens this dialog and always ends in a person pressing the button
 * ([ADR 0011](../../../../docs/adr/0011-one-command-path-for-every-actor.md)): there is no path from the Studio to
 * somebody's Instagram that does not go through a human looking at what is about to go out.
 *
 * **Post now and Schedule are the same thing**, and that is the design, not a shortcut. Both write a row; the only
 * difference is the time on it. Nothing here talks to a platform — the worker does that — so "now" means "in about
 * a minute", which the dialog says rather than pretending otherwise.
 *
 * A channel a platform would refuse is shown with the reason and cannot be ticked. The reason comes from
 * `refusedBecause` in contracts, which is the same function the API checks with, so the screen and the server never
 * disagree about why something cannot go.
 *
 * **TikTok asks its own questions, and asks them here** ([32](../../../../docs/32-what-each-network-requires.md)).
 * Its Content Sharing Guidelines, which its audit checks this screen against, require the account's name, a preview,
 * a privacy choice with nothing selected, the interactions unticked and greyed out where the account has them off,
 * a commercial-content switch in TikTok's words, the consent sentence and a word about processing. The rules are
 * `tiktokRefusal` in contracts — the same function the API and the connector ask.
 *
 * **Each network's questions sit in its own row** (2026-09-29). It was one long column — the preview, the words, five
 * ticks, then a TikTok block and a YouTube block under all of them — and the owner found it a mess on live. Now the
 * preview sits beside the words, ticking a network opens its questions and its refusals under its own name, and the
 * button stays at the foot of the dialog however long the questions make it.
 */

/**
 * What is about to go out: a Studio project's finished render, or a video imported on the Content tab, which has no
 * project at all (2026-09-26). The dialog needs only these things, so both arrive the same way. What it says is not
 * one of them: the dialog reads that from the project itself (`captionOf`).
 */
export interface Publishable {
  projectId: string | null;
  /** With a video's length and size where they are known: each platform takes only so much (`PLATFORMS`). */
  media: {
    id: string; kind: string; durationMs?: number | null; bytes?: number | null;
    /** A picture's shape: Instagram takes only 4:5 to 1.91:1 (32). */
    width?: number | null; height?: number | null;
  } | null;
  /** The day it was planned for, when it came from Prepare my week: "later" starts there rather than tomorrow. */
  planFor?: string | null;
}

/** A Studio project as something to publish: the newest render that finished. */
export const publishableOf = (project: ProjectView): Publishable => {
  const output = project.renders.find((r) => r.status === "succeeded" && r.output)?.output ?? null;
  return {
    projectId: project.id, planFor: project.planFor,
    media: output ? { id: output.id, kind: output.kind, durationMs: output.durationMs, bytes: output.bytes, width: output.width, height: output.height } : null,
  };
};

/**
 * What a post starts out saying: the words the piece itself says — its spoken lines, or where nobody speaks the words
 * on screen — each once, as sentences. Never the project's title, which is the brief cut at sixty characters: on live
 * on 2026-09-29 the box opened on "A short vertical video saying Joogoos is here: the whole of". A piece that says
 * nothing starts empty, with a prompt, rather than with words nobody meant to post.
 */
export function captionOf(project: ProjectView | undefined): string {
  const shots = [...(project?.shots ?? [])].sort((a, b) => a.position - b.position);
  const lines = (pick: (s: ShotView) => string | null) =>
    [...new Set(shots.map((s) => pick(s)?.trim().replace(/\s+/g, " ")).filter((t): t is string => Boolean(t)).map(asSentence))];
  const said = lines((s) => s.dialogue);
  return (said.length ? said : lines((s) => s.onScreenText)).join(" ");
}

const asSentence = (t: string) => (/[.!?…]$/.test(t) ? t : `${t}.`);

export function PublishDialog({ piece, when: startsOn, onClose }: {
  piece: Publishable;
  /**
   * Which of the two the dialog starts on: `now` posts in a minute, `later` asks for a day and a time. Either way the
   * person can switch inside it (2026-09-29, the owner: one Post button, now or later in its window). Unsaid, it
   * starts on now — or on later when the piece was planned for a day still ahead.
   */
  when?: "now" | "later";
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const channels = useQuery({ queryKey: ["channels"], queryFn: () => api.channels() });
  const media = piece.media;

  const [chosen, setChosen] = useState<string[]>([]);
  // The piece's own words, once its project is read: the Studio has it already, the Content tab asks once. Null until
  // somebody types, so words that arrive a moment after the dialog opens still land in the box.
  const project = useQuery({ queryKey: ["project", piece.projectId], queryFn: () => api.project(piece.projectId!), enabled: Boolean(piece.projectId) });
  const [typed, setTyped] = useState<string | null>(null);
  const caption = typed ?? captionOf(project.data);
  // TikTok's choices start empty: no privacy level, nothing allowed, nothing disclosed (TikTok's rules).
  const [tiktok, setTiktok] = useState<Partial<TikTokChoices>>({});
  // YouTube's made-for-kids starts unanswered: its policies require the uploader to say (32). Public is the start.
  // Neither of YouTube's answers is assumed: privacy started at Public, which an unwatched Post then published (2026-09-29).
  const [youtube, setYoutube] = useState<Partial<YouTubeChoices>>({});
  const workspaceId = useWorkspace().data?.id ?? null;
  // A planned piece starts on its own day, while that day is still ahead; everything else, this time tomorrow.
  const [plannedAhead] = useState(() => {
    const planned = piece.planFor ? new Date(piece.planFor) : null;
    return planned && planned.getTime() > Date.now() + POST_MIN_LEAD_MS ? planned : null;
  });
  const [at, setAt] = useState(() => localAt(plannedAhead ?? new Date(Date.now() + 24 * 3_600_000)));
  const [when, setWhen] = useState<"now" | "later">(startsOn ?? (plannedAhead ? "later" : "now"));

  const post = useMutation({
    mutationFn: () => api.schedulePost({
      channelIds: chosen,
      ...(piece.projectId ? { projectId: piece.projectId } : {}),
      ...(media ? { mediaId: media.id } : {}),
      caption,
      ...(toTiktok || toYoutube ? { options: { ...(toTiktok ? { tiktok } : {}), ...(toYoutube ? { youtube: youtubeChoices } : {}) } } : {}),
      // A minute from now for "post now", which is the soonest the sweeper will see it. Anything less would be
      // refused by the API, and a dialog that offers a time the server will not take is a dialog that lies.
      scheduledAt: when === "now" ? new Date(Date.now() + POST_MIN_LEAD_MS + 5_000).toISOString() : new Date(at).toISOString(),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      void qc.invalidateQueries({ queryKey: ["channels"] });
      onClose();
    },
  });

  const shape = {
    hasMedia: Boolean(media), isVideo: media?.kind === "video", caption,
    durationMs: media?.durationMs, bytes: media?.bytes, width: media?.width, height: media?.height,
  };
  const connected = (channels.data?.channels ?? []).filter((c) => c.status === "connected");
  const refusals = new Map(connected.map((c) => [c.id, refusedBecause(c.platform, shape)]));
  // A channel that was ticked and has since become impossible — usually by the caption growing — is unticked here
  // rather than refused on Send, so the button never fails for a reason the screen was already showing.
  const going = chosen.filter((id) => refusals.get(id) === null);
  const tiktokChannel = connected.find((c) => c.platform === "tiktok") ?? null;
  const toTiktok = Boolean(tiktokChannel && going.includes(tiktokChannel.id));
  const creator = useQuery({
    queryKey: ["tiktok-creator", tiktokChannel?.id],
    queryFn: () => api.tiktokCreator(tiktokChannel!.id),
    enabled: toTiktok,
    staleTime: 60_000,
  });
  // TikTok going too means TikTok's rules must hold, against the account as TikTok describes it right now.
  const youtubeChannel = connected.find((c) => c.platform === "youtube") ?? null;
  const toYoutube = Boolean(youtubeChannel && going.includes(youtubeChannel.id));
  // The channel's usual answer, set once on the Channels row, until this video is answered differently (32).
  const usualKids = youtube.madeForKids ?? youtubeChannel?.madeForKids ?? null;
  const youtubeChoices: Partial<YouTubeChoices> = usualKids === null ? youtube : { ...youtube, madeForKids: usualKids };
  const youtubeNo = toYoutube ? youtubeRefusal(youtubeChoices) : null;
  const tiktokNo = toTiktok
    ? (creator.data ? tiktokRefusal(tiktok, creator.data, { durationMs: media?.durationMs ?? null }) : creator.isError ? "TikTok did not answer. Try again in a moment." : "…")
    : null;

  const xGoing = connected.some((c) => c.platform === "x" && going.includes(c.id));
  // What each network asks, and what it refuses, drawn inside that network's row once it is ticked.
  const optionsOf = (channel: ChannelView) => {
    if (channel.platform === "tiktok") {
      return (
        <>
          <TikTokSection creator={creator.data ?? null} failed={creator.isError} choices={tiktok} onChange={setTiktok} />
          {tiktokNo && tiktokNo !== "…" ? <p className="text-xs text-bad">{tiktokNo}</p> : null}
        </>
      );
    }
    if (channel.platform === "youtube") {
      return (
        <>
          <YouTubeSection choices={youtubeChoices} usual={youtubeChannel?.madeForKids ?? null} onChange={setYoutube} />
          {youtubeNo ? <p className="text-xs text-bad">{youtubeNo}</p> : null}
        </>
      );
    }
    if (channel.platform === "x" && xGoing && hasLink(caption)) return <p className="text-xs text-muted">{X_LINK_NOTE}</p>;
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/30 p-0 sm:p-6" onClick={onClose}>
      <div
        className="w-full sm:max-w-[600px] max-h-full overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-panel border border-line flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Post"
      >
        <div className="px-5 pt-5 pb-4 flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Post</h2>
            <button onClick={onClose} className="ml-auto text-muted" aria-label="Close"><Icon name="x" size={16} /></button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {media ? <Preview media={media} workspaceId={workspaceId} /> : null}
            <label className="flex-1 min-w-0 flex flex-col gap-1">
              <span className={HEADING}>What it says</span>
              <textarea
                value={caption}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="What the post says, in a line or two."
                rows={media ? 7 : 4}
                className="flex-1 px-3 py-2 rounded-lg border border-line-2 bg-page text-sm resize-y"
              />
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={HEADING}>Where it goes</span>
            {channels.isPending ? <span className="text-sm text-faint">…</span> : null}
            {!channels.isPending && !connected.length ? (
              <p className="text-[13px] text-muted leading-relaxed">
                Nothing is connected yet. Connect an account under <b>Business → Channels</b> and this post can go out
                on it.
              </p>
            ) : null}
            {connected.map((channel) => (
              <Choice
                key={channel.id}
                channel={channel}
                refused={refusals.get(channel.id) ?? null}
                on={chosen.includes(channel.id)}
                onToggle={() => setChosen((was) => was.includes(channel.id) ? was.filter((x) => x !== channel.id) : [...was, channel.id])}
              >
                {optionsOf(channel)}
              </Choice>
            ))}
          </div>

          <fieldset className="flex flex-col gap-1.5">
            <legend className={`${HEADING} mb-1.5`}>When</legend>
            <div className="flex flex-wrap items-center gap-2">
              {(["now", "later"] as const).map((w) => (
                <label key={w} className={`px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${when === w ? "border-ink bg-ink text-white" : "border-line-2 hover:bg-page"}`}>
                  <input type="radio" name="when" value={w} checked={when === w} onChange={() => setWhen(w)} className="sr-only" />
                  {w === "now" ? "Now" : "Later"}
                </label>
              ))}
              {when === "later" ? (
                <input
                  type="datetime-local"
                  aria-label="Day and time"
                  value={at}
                  min={localAt(new Date(Date.now() + POST_MIN_LEAD_MS))}
                  onChange={(e) => setAt(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-line-2 bg-page text-sm"
                />
              ) : null}
            </div>
          </fieldset>
        </div>

        {/* The button stays in view however long the networks' questions make the dialog. */}
        <div className="sticky bottom-0 mt-auto px-5 py-3 border-t border-line bg-panel flex flex-col gap-2">
          {post.error ? <p className="text-xs text-bad">{String(post.error)}</p> : null}
          <div className="flex items-center gap-2">
            <button
              onClick={() => post.mutate()}
              disabled={!going.length || post.isPending || Boolean(tiktokNo) || Boolean(youtubeNo)}
              className="px-4 py-2 rounded-lg bg-coral text-white text-sm font-semibold disabled:opacity-40"
            >
              {when === "now" ? "Post it" : "Schedule it"}
              {going.length > 1 ? ` to ${going.length}` : ""}
            </button>
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-line-2 text-sm">Not yet</button>
            {when === "now" ? <span className="ml-auto text-xs text-muted text-right">It goes out in about a minute. You can take it back until it does.</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

const HEADING = "text-[11px] font-semibold uppercase tracking-[.06em] text-muted";

/** What is about to go out, as it will look: the video itself, or the picture. TikTok requires it; everyone gains. */
function Preview({ media, workspaceId }: { media: NonNullable<Publishable["media"]>; workspaceId: string | null }) {
  const url = mediaFileUrl(media.id, workspaceId);
  return (
    <div className="sm:w-40 shrink-0 rounded-lg border border-line bg-page p-2 flex items-center justify-center" aria-label="Preview">
      {media.kind === "video"
        ? <video src={url} controls muted playsInline className="max-h-56 w-full rounded object-contain" />
        : <img src={mediaFileUrl(media.id, workspaceId, "thumb")} alt="" className="max-h-56 w-full rounded object-contain" />}
    </div>
  );
}

/**
 * TikTok's questions, in TikTok's order and TikTok's words (32). Nothing is pre-selected and nothing is remembered:
 * each post starts empty, because TikTok's audit forbids deciding any of it for somebody.
 */
function TikTokSection({ creator, failed, choices, onChange }: {
  creator: TikTokCreator | null;
  failed: boolean;
  choices: Partial<TikTokChoices>;
  onChange: (c: Partial<TikTokChoices>) => void;
}) {
  const set = (patch: Partial<TikTokChoices>) => onChange({ ...choices, ...patch });
  const branded = Boolean(choices.disclose && choices.brandedContent);
  const label = tiktokLabel({ disclose: Boolean(choices.disclose), yourBrand: Boolean(choices.yourBrand), brandedContent: Boolean(choices.brandedContent) });
  const consent = tiktokConsent({ disclose: Boolean(choices.disclose), brandedContent: Boolean(choices.brandedContent) });

  if (failed) return <p className="text-xs text-bad" role="group" aria-label="TikTok">TikTok did not say what this account can post. Close this and try again in a moment.</p>;
  if (!creator) return <p className="text-xs text-faint" role="group" aria-label="TikTok">Asking TikTok about the account…</p>;

  const interaction = (key: "allowComment" | "allowDuet" | "allowStitch", word: string, off: boolean) => (
    <label className={`flex items-center gap-1.5 text-[12.5px] ${off ? "opacity-50" : "cursor-pointer"}`} title={off ? `${word} is turned off on this TikTok account.` : undefined}>
      <input
        type="checkbox" className="accent-coral" disabled={off}
        checked={Boolean(choices[key]) && !off}
        onChange={(e) => set({ [key]: e.target.checked })}
      />
      Allow {word.toLowerCase()}
    </label>
  );

  return (
    <div className="flex flex-col gap-3" role="group" aria-label="TikTok">
      <div className="text-[12.5px]">Posting to <b>{creator.nickname}</b></div>
      {!creator.canPost ? <p className="text-xs text-bad">{creator.cannotPostBecause ?? TIKTOK_TEXT.cannotPost}</p> : null}

      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium">Who can see this video</span>
        <select
          value={choices.privacyLevel ?? ""}
          onChange={(e) => set({ privacyLevel: e.target.value as TikTokPrivacy })}
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]"
          aria-label="Who can see this video"
        >
          <option value="" disabled>Choose…</option>
          {creator.privacyOptions.map((p) => (
            <option key={p} value={p} disabled={branded && p === "SELF_ONLY"} title={branded && p === "SELF_ONLY" ? TIKTOK_TEXT.brandedNotPrivate : undefined}>
              {TIKTOK_PRIVACY_WORDS[p]}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {interaction("allowComment", "Comments", creator.commentDisabled)}
        {interaction("allowDuet", "Duet", creator.duetDisabled)}
        {interaction("allowStitch", "Stitch", creator.stitchDisabled)}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-2 text-[12.5px] font-medium cursor-pointer">
          <input
            type="checkbox" role="switch" className="accent-coral"
            checked={Boolean(choices.disclose)}
            onChange={(e) => set({ disclose: e.target.checked, ...(e.target.checked ? {} : { yourBrand: false, brandedContent: false }) })}
          />
          {TIKTOK_TEXT.disclose}
        </label>
        <p className="text-[11.5px] text-muted leading-relaxed">{TIKTOK_TEXT.discloseHint}</p>
        {choices.disclose ? (
          <div className="flex flex-col gap-1.5 pl-5">
            <label className="flex items-start gap-2 text-[12.5px] cursor-pointer">
              <input type="checkbox" className="accent-coral mt-0.5" checked={Boolean(choices.yourBrand)} onChange={(e) => set({ yourBrand: e.target.checked })} />
              <span><b className="font-medium">{TIKTOK_TEXT.yourBrand}</b><span className="block text-[11.5px] text-muted">{TIKTOK_TEXT.yourBrandHint}</span></span>
            </label>
            <label
              className={`flex items-start gap-2 text-[12.5px] ${choices.privacyLevel === "SELF_ONLY" ? "opacity-50" : "cursor-pointer"}`}
              title={choices.privacyLevel === "SELF_ONLY" ? TIKTOK_TEXT.brandedNotPrivate : undefined}
            >
              <input
                type="checkbox" className="accent-coral mt-0.5" disabled={choices.privacyLevel === "SELF_ONLY"}
                checked={Boolean(choices.brandedContent)} onChange={(e) => set({ brandedContent: e.target.checked })}
              />
              <span><b className="font-medium">{TIKTOK_TEXT.brandedContent}</b><span className="block text-[11.5px] text-muted">{TIKTOK_TEXT.brandedContentHint}</span></span>
            </label>
            {label ? <p className="text-[11.5px] text-ink-2">{label}</p> : <p className="text-[11.5px] text-muted">{TIKTOK_TEXT.chooseOne}</p>}
          </div>
        ) : null}
      </div>

      <p className="text-[11.5px] text-muted leading-relaxed">
        {consent.includes("Branded Content Policy") ? (
          <>By posting, you agree to TikTok's <a href={TIKTOK_BRANDED_CONTENT_URL} target="_blank" rel="noreferrer" className="underline">Branded Content Policy</a> and <a href={TIKTOK_MUSIC_USAGE_URL} target="_blank" rel="noreferrer" className="underline">Music Usage Confirmation</a>.</>
        ) : (
          <>By posting, you agree to TikTok's <a href={TIKTOK_MUSIC_USAGE_URL} target="_blank" rel="noreferrer" className="underline">Music Usage Confirmation</a>.</>
        )}
      </p>
      <p className="text-[11.5px] text-muted">{TIKTOK_TEXT.processing}</p>
    </div>
  );
}

/**
 * YouTube's questions (32): whether the video is made for kids, which its policies require the uploader to answer
 * and which is never assumed, and who can see it. The link to YouTube's terms is the API policies' requirement too.
 */
function YouTubeSection({ choices, usual, onChange }: {
  choices: Partial<YouTubeChoices>;
  /** The channel's usual answer, or null when it asks every time. */
  usual: boolean | null;
  onChange: (c: Partial<YouTubeChoices>) => void;
}) {
  const set = (patch: Partial<YouTubeChoices>) => onChange({ ...choices, ...patch });
  return (
    <div className="flex flex-col gap-3" role="group" aria-label="YouTube">
      <fieldset className="flex flex-col gap-1">
        <legend className="text-[12px] font-medium">{YOUTUBE_TEXT.madeForKids}</legend>
        <p className="text-[11.5px] text-muted leading-relaxed">
          {usual === null ? YOUTUBE_TEXT.madeForKidsHint : "Your channel's usual answer, set under Business → Channels. Change it here for this video only."}
        </p>
        <div className="flex gap-4 text-[12.5px]">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name="made-for-kids" className="accent-coral" checked={choices.madeForKids === true} onChange={() => set({ madeForKids: true })} />
            Yes, it's made for kids
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name="made-for-kids" className="accent-coral" checked={choices.madeForKids === false} onChange={() => set({ madeForKids: false })} />
            No, it's not made for kids
          </label>
        </div>
      </fieldset>
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium">Who can see it on YouTube</span>
        <select
          value={choices.privacy ?? ""}
          onChange={(e) => set({ privacy: e.target.value as YouTubePrivacy })}
          aria-label="Who can see it on YouTube"
          className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]"
        >
          <option value="" disabled>Choose…</option>
          {YOUTUBE_PRIVACY.map((p) => <option key={p} value={p}>{YOUTUBE_PRIVACY_WORDS[p]}</option>)}
        </select>
      </label>
      <p className="text-[11.5px] text-muted">
        Uploading to YouTube means agreeing to the{" "}
        <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer" className="underline">YouTube Terms of Service</a>.
      </p>
    </div>
  );
}

/**
 * One channel, ticked or not — and, where a platform would not take this post, why not instead of a tick. Once ticked,
 * what that network asks opens under its name. It sits beside the label rather than in it: inside a label, a press on
 * TikTok's privacy menu would untick TikTok.
 */
function Choice({ channel, refused, on, onToggle, children }: {
  channel: ChannelView;
  refused: string | null;
  on: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  const open = on && !refused && Boolean(children);
  return (
    <div className={`rounded-lg border bg-page ${refused ? "border-line opacity-60" : on ? "border-ink/30" : "border-line-2"}`}>
      <label className={`flex items-start gap-2.5 px-3 py-2 ${refused ? "" : "cursor-pointer"}`}>
        <input type="checkbox" checked={on && !refused} disabled={Boolean(refused)} onChange={onToggle} className="mt-0.5 accent-coral" />
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium">
            {PLATFORMS[channel.platform].label}
            <span className="font-normal text-muted"> · {channel.handle ? `@${channel.handle}` : channel.accountName}</span>
          </span>
          {refused ? <span className="block text-xs text-muted mt-0.5">{refused}</span> : null}
        </span>
      </label>
      {open ? <div className="border-t border-line px-3 py-3 pl-9 flex flex-col gap-2">{children}</div> : null}
    </div>
  );
}

/** A date in the shape `datetime-local` wants, in the browser's own clock rather than UTC. */
export function localAt(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
